import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { useMemo, useCallback, useEffect } from 'react';
import monitoring from '@clearscore-group/lib.helpers.monitoring';
import { useSelector } from 'react-redux';
import { selectors } from '@clearscore-group/lib.redux.market';
import getDevice, { getBrowser } from '@clearscore-group/lib.helpers.device';
import { useLocation } from 'react-router-dom';
import usePrevious from '@clearscore-group/lib.hooks.use-previous';
var blacklistedSearchParams = ['email', 'firstName', 'lastName', 'dob'];

/* eslint-disable import/prefer-default-export */
var getSanitisedUrl = function getSanitisedUrl() {
  var _window2 = window,
    location = _window2.location;
  var url = new URL(location.href);
  blacklistedSearchParams.forEach(function (p) {
    return url.searchParams.delete(p);
  });
  var search = !url.search && location.search ? '?' : url.search;
  var urlString = "".concat(url.protocol, "//").concat(url.host).concat(url.pathname).concat(search);
  return {
    url: urlString,
    search: search
  };
};
var _device;
var device = function device() {
  if (!_device) {
    _device = getDevice();
  }
  return _device;
};

/**
 * Hook to get the default segment props that must be pulled from the redux store
 */
var useDefaultSegmentProps = function useDefaultSegmentProps() {
  var market = useSelector(selectors.getActiveMarket);
  var browser = getBrowser();
  return useMemo(function () {
    return {
      device: device(),
      cs_site: market,
      browserName: browser.name,
      browserVersion: browser.version
    };
  }, [market, browser]);
};

/**
 * Get the default segment props that must be generated at event send time
 */
var getWindowSegmentProps = function getWindowSegmentProps() {
  var _getSanitisedUrl = getSanitisedUrl(),
    url = _getSanitisedUrl.url;
  return {
    location: url,
    referrer: document.referrer || 'Direct'
  };
};
var useTracking = function useTracking() {
  var defaultSegmentProps = useDefaultSegmentProps();
  var track = useCallback(function () {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      name = _ref.name,
      _ref$props = _ref.props,
      _props = _ref$props === void 0 ? {} : _ref$props,
      _ref$event = _ref.event,
      _event = _ref$event === void 0 ? 'track' : _ref$event,
      onComplete = _ref.onComplete;
    var _window, _window$analytics;
    var hasTracking = typeof ((_window = window) === null || _window === void 0 ? void 0 : (_window$analytics = _window.analytics) === null || _window$analytics === void 0 ? void 0 : _window$analytics.track) === 'function';
    if (!hasTracking && monitoring.hasMonitoring()) {
      monitoring.log("Cannot send tracking event ".concat(name, " - no window.analytics available"));
    }
    if (!hasTracking) {
      return;
    }
    var hasFiredOnComplete = false;
    var fallbackTimeout = setTimeout(function () {
      if (onComplete && !hasFiredOnComplete) onComplete();
    }, 100);
    var customOnComplete = function customOnComplete() {
      hasFiredOnComplete = true;
      clearTimeout(fallbackTimeout);
      onComplete();
    };
    window.analytics[_event](name, _objectSpread(_objectSpread(_objectSpread({}, getWindowSegmentProps()), defaultSegmentProps), _props),
    // analytics.js "options" object - not needed for us (yet!)
    {},
    // An optional function called when the analytics request completes
    onComplete ? customOnComplete : undefined);
  }, [defaultSegmentProps]);
  return track;
};
var usePageTracking = function usePageTracking() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    hasPageTracking = _ref2.hasPageTracking,
    setLocation = _ref2.location,
    props = _ref2.props;
  var _location$state;
  var track = useTracking();
  var reactRouterLocation = useLocation();
  var location = setLocation || reactRouterLocation;
  var referrer = usePrevious(location.pathname);
  var _ref3 = (location === null || location === void 0 ? void 0 : (_location$state = location.state) === null || _location$state === void 0 ? void 0 : _location$state.tracking) || {},
    source = _ref3.source,
    _ref3$props = _ref3.props,
    stateProps = _ref3$props === void 0 ? {} : _ref3$props;
  useEffect(function () {
    if (hasPageTracking) {
      track({
        event: 'page',
        props: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getSanitisedUrl()), stateProps), props), {}, {
          referrer: referrer,
          source: source
        })
      });
    }
  }, [location === null || location === void 0 ? void 0 : location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useTracking;
export { usePageTracking };